/**
 * DualQuaternionSkinningNode — dual-quaternion skinning in the VERTEX stage
 * (shader wave stage 4, 2026-09-09), after Kavan et al. 2008 "Geometric
 * Skinning with Approximate Dual Quaternion Blending" (the DLB blend with the
 * antipodality fix, §4). The CPU port lives beside it
 * (`DualQuaternionSkinning.ts`); this is the TSL twin a node material runs.
 *
 * three's node materials always run linear-blend `skinning(object)` first
 * (NodeMaterial.setupPosition) and then let `positionNode` overwrite the
 * result — so this reads the ORIGINAL position/normal attributes, converts
 * each of the vertex's four bone matrices to a unit dual quaternion in the
 * shader (rotation → quaternion by the branch-free copysign form, dual part
 * = ½·t·r), blends with the skin weights after flipping any bone whose real
 * part opposes the first (Kavan §4.1), normalises by the real part's length
 * and applies the rigid transform — no candy-wrapper collapse at a twisted
 * wrist, no bulge at a bent elbow. The bone buffer is three's own
 * `skeleton.boneMatrices` (three keeps updating the skeleton through its
 * linear pass, whose result is discarded).
 *
 * `dqFromMatrixCpu` and `dqBlendCpu` are the tests' oracles for the shader's
 * arithmetic (the CPU port is the reference).
 */

import type * as THREE from 'three/webgpu';
import {
  Fn, float, vec3, vec4, attribute, reference, referenceBuffer, dot, cross, sqrt, max, varying,
  transformNormalToView, select,
} from 'three/tsl';

/**
 * Quaternion (x,y,z,w) from a rotation matrix given by its three COLUMNS
 * (branch-free copysign form). Columns are normalised first: a skinned
 * hierarchy under a fit-scaled root hands three's `boneMatrices` a UNIFORM
 * scale, and the sqrt(1 + trace) form needs unit columns.
 */
export function quatFromColumnsCpu(c0in: number[], c1in: number[], c2in: number[]): [number, number, number, number] {
  const n = (c: number[]): number[] => { const l = Math.hypot(c[0], c[1], c[2]) || 1; return [c[0] / l, c[1] / l, c[2] / l]; };
  const c0 = n(c0in), c1 = n(c1in), c2 = n(c2in);
  const m00 = c0[0], m10 = c0[1], m20 = c0[2], m01 = c1[0], m11 = c1[1], m21 = c1[2], m02 = c2[0], m12 = c2[1], m22 = c2[2];
  const w = Math.sqrt(Math.max(0, 1 + m00 + m11 + m22)) / 2;
  let x = Math.sqrt(Math.max(0, 1 + m00 - m11 - m22)) / 2;
  let y = Math.sqrt(Math.max(0, 1 - m00 + m11 - m22)) / 2;
  let z = Math.sqrt(Math.max(0, 1 - m00 - m11 + m22)) / 2;
  x = (m21 - m12) >= 0 ? x : -x;
  y = (m02 - m20) >= 0 ? y : -y;
  z = (m10 - m01) >= 0 ? z : -z;
  return [x, y, z, w];
}

/** Quaternion product a·b (x,y,z,w). */
export function quatMulCpu(a: number[], b: number[]): [number, number, number, number] {
  return [
    a[3] * b[0] + a[0] * b[3] + a[1] * b[2] - a[2] * b[1],
    a[3] * b[1] - a[0] * b[2] + a[1] * b[3] + a[2] * b[0],
    a[3] * b[2] + a[0] * b[1] - a[1] * b[0] + a[2] * b[3],
    a[3] * b[3] - a[0] * b[0] - a[1] * b[1] - a[2] * b[2],
  ];
}

/** Dual quaternion {r, d} (+ the uniform scale `s` of the columns) from a column-major mat4. */
export function dqFromMatrixCpu(m: ArrayLike<number>): { r: [number, number, number, number]; d: [number, number, number, number]; s: number } {
  const r = quatFromColumnsCpu([m[0], m[1], m[2]], [m[4], m[5], m[6]], [m[8], m[9], m[10]]);
  const t = [m[12], m[13], m[14], 0];
  const td = quatMulCpu(t, r);
  return { r, d: [td[0] * 0.5, td[1] * 0.5, td[2] * 0.5, td[3] * 0.5], s: Math.hypot(m[0], m[1], m[2]) || 1 };
}

/** DLB blend with the antipodality fix, then normalisation by |r|. */
export function dqBlendCpu(dqs: Array<{ r: number[]; d: number[]; s?: number }>, w: number[]): { r: number[]; d: number[]; s: number } {
  const r = [0, 0, 0, 0], d = [0, 0, 0, 0];
  const r0 = dqs[0].r;
  for (let i = 0; i < dqs.length; i++) {
    const s = (r0[0] * dqs[i].r[0] + r0[1] * dqs[i].r[1] + r0[2] * dqs[i].r[2] + r0[3] * dqs[i].r[3]) < 0 ? -w[i] : w[i];
    for (let k = 0; k < 4; k++) { r[k] += dqs[i].r[k] * s; d[k] += dqs[i].d[k] * s; }
  }
  const len = Math.hypot(r[0], r[1], r[2], r[3]) || 1;
  return { r: r.map((v) => v / len), d: d.map((v) => v / len), s: (dqs[0] as { s?: number }).s ?? 1 };
}

/** Apply a unit dual quaternion (+ optional uniform scale) to a point. */
export function dqTransformPointCpu(dq: { r: number[]; d: number[]; s?: number }, p: number[]): [number, number, number] {
  const [rx, ry, rz, rw] = dq.r, [dx, dy, dz, dw] = dq.d;
  const sc = dq.s ?? 1;
  const cx = ry * p[2] - rz * p[1] + rw * p[0], cy = rz * p[0] - rx * p[2] + rw * p[1], cz = rx * p[1] - ry * p[0] + rw * p[2];
  const rot = [sc * (p[0] + 2 * (ry * cz - rz * cy)), sc * (p[1] + 2 * (rz * cx - rx * cz)), sc * (p[2] + 2 * (rx * cy - ry * cx))];
  const tx = 2 * (rw * dx - dw * rx + ry * dz - rz * dy), ty = 2 * (rw * dy - dw * ry + rz * dx - rx * dz), tz = 2 * (rw * dz - dw * rz + rx * dy - ry * dx);
  return [rot[0] + tx, rot[1] + ty, rot[2] + tz];
}

/**
 * Patch a node material so a SkinnedMesh deforms by dual-quaternion blending.
 * Call once per (material, skinned mesh) — the bone count fixes the buffer.
 */
export function applyDualQuaternionSkinning(
  material: THREE.Material & { positionNode?: unknown; normalNode?: unknown },
  skinnedMesh: THREE.SkinnedMesh,
): void {
  const boneCount = skinnedMesh.skeleton.bones.length;
  const build = () => {
    const skinIndex = attribute('skinIndex', 'uvec4');
    const skinWeight = attribute('skinWeight', 'vec4');
    // Bound to the RENDERED object (three's ReferenceNode falls back to
    // builder.object when none is given), so one patched material serves every
    // actor of a body — the same bone count is all the buffer needs.
    const bindMatrix = reference('bindMatrix', 'mat4', null as never);
    const bindMatrixInverse = reference('bindMatrixInverse', 'mat4', null as never);
    const bones = referenceBuffer('skeleton.boneMatrices', 'mat4', boneCount, null as never);

    const toDq = (m: unknown) => {
      const mm = m as { element(i: number): { xyz: ReturnType<typeof vec3> } };
      // Normalise the rotation columns: a fit-scaled actor root puts a uniform
      // scale into three's boneMatrices, and the copysign quaternion needs unit
      // columns; the scale rides along and re-scales the skinned point.
      const c0r = mm.element(0).xyz, c1r = mm.element(1).xyz, c2r = mm.element(2).xyz, t = mm.element(3).xyz;
      const sc = max(c0r.length(), float(1e-6));
      const c0 = c0r.div(sc), c1 = c1r.div(max(c1r.length(), float(1e-6))), c2 = c2r.div(max(c2r.length(), float(1e-6)));
      const w = sqrt(max(float(0), float(1).add(c0.x).add(c1.y).add(c2.z))).mul(0.5);
      const x0 = sqrt(max(float(0), float(1).add(c0.x).sub(c1.y).sub(c2.z))).mul(0.5);
      const y0 = sqrt(max(float(0), float(1).sub(c0.x).add(c1.y).sub(c2.z))).mul(0.5);
      const z0 = sqrt(max(float(0), float(1).sub(c0.x).sub(c1.y).add(c2.z))).mul(0.5);
      const x = select(c1.z.sub(c2.y).greaterThanEqual(0), x0, x0.negate());
      const y = select(c2.x.sub(c0.z).greaterThanEqual(0), y0, y0.negate());
      const z = select(c0.y.sub(c1.x).greaterThanEqual(0), z0, z0.negate());
      const r = vec4(x, y, z, w);
      // d = ½ · (t, 0) · r
      const d = vec4(
        t.x.mul(r.w).add(t.y.mul(r.z)).sub(t.z.mul(r.y)),
        t.y.mul(r.w).sub(t.x.mul(r.z)).add(t.z.mul(r.x)),
        t.z.mul(r.w).add(t.x.mul(r.y)).sub(t.y.mul(r.x)),
        t.x.mul(r.x).add(t.y.mul(r.y)).add(t.z.mul(r.z)).negate(),
      ).mul(0.5);
      return { r, d, sc };
    };
    const b0 = toDq(bones.element(skinIndex.x)), b1 = toDq(bones.element(skinIndex.y)), b2 = toDq(bones.element(skinIndex.z)), b3 = toDq(bones.element(skinIndex.w));
    const sgn = (bi: { r: ReturnType<typeof vec4> }, wgt: ReturnType<typeof float>) => select(dot(b0.r, bi.r).lessThan(0), wgt.negate(), wgt);
    const w0 = skinWeight.x, w1 = sgn(b1, skinWeight.y), w2 = sgn(b2, skinWeight.z), w3 = sgn(b3, skinWeight.w);
    const rSum = b0.r.mul(w0).add(b1.r.mul(w1)).add(b2.r.mul(w2)).add(b3.r.mul(w3));
    const dSum = b0.d.mul(w0).add(b1.d.mul(w1)).add(b2.d.mul(w2)).add(b3.d.mul(w3));
    const len = max(rSum.length(), float(1e-6));
    const r = rSum.div(len), d = dSum.div(len);
    const rv = r.xyz;
    type N = { add(x: unknown): N; mul(x: unknown): N; negate(): N; xyz?: N; x?: N; y?: N; z?: N; w?: N };
    const rotate = (vv: unknown): N => {
      const v = vec3(vv as never) as unknown as N;
      const inner = (cross(rv as never, v as never) as unknown as N).add(v.mul(r.w));
      return v.add((cross(rv as never, inner as never) as unknown as N).mul(2));
    };
    const trans = (rv as unknown as N).mul(d.w).negate().add((d.xyz as unknown as N).mul(r.w)).add(cross(rv as never, d.xyz as never)).mul(2);

    const p = bindMatrix.mul(vec4(attribute('position', 'vec3') as never, 1)).xyz;
    const skinnedP = rotate(p).mul(b0.sc).add(trans); // the hierarchy's uniform scale (bone X's; a vertex's bones share it)
    const n = bindMatrix.mul(vec4(attribute('normal', 'vec3') as never, 0)).xyz;
    const skinnedN = rotate(n);
    return { position: bindMatrixInverse.mul(vec4(skinnedP as never, 1)).xyz, normal: bindMatrixInverse.mul(vec4(skinnedN as never, 0)).xyz };
  };

  const dq = build();
  material.positionNode = Fn(() => dq.position)();
  material.normalNode = Fn(() => transformNormalToView(varying(dq.normal as never, 'v_dqNormal') as never))();
  (material as { needsUpdate: boolean }).needsUpdate = true;
}
